import React, { useEffect, useState } from "react";
import { ArrowUpRight, ArrowDownRight, Clock, TrendingUp, Layers, Activity, Wifi, BookOpen, TimerReset } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function SignalCard({ signal }) {
  const { t, lang } = useLang();
  if (!signal) return null;
  const isCall = signal.direction === "CALL";
  const color = isCall ? "#00FFC2" : "#EC1E9C";
  const glow = isCall ? "rgba(0, 255, 194,0.25)" : "rgba(236, 30, 156,0.25)";
  const entry = signal.entry_time ? new Date(signal.entry_time) : null;
  const stakes = (signal.martingale || []).map((m) => `${m.stake}`).join(" → ");
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString(lang === "it" ? "it-IT" : "en-GB", { hour: "2-digit", minute: "2-digit" });

  return (
    <div data-testid="active-signal-card" className="signal-in rounded-xl border bg-[#121824] overflow-hidden"
      style={{ borderColor: color, boxShadow: `0 0 40px ${glow}` }}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#243047]" style={{ background: glow }}>
        <div className="flex items-center gap-2">
          <span data-testid="signal-asset-name" className="font-heading text-lg font-bold text-white">{signal.asset_name}</span>
          <span data-testid="signal-source-badge" className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#94A3B8] border border-[#243047] rounded px-1.5 py-0.5">
            <Wifi className="w-3 h-3" /> {signal.data_source === "pocketoption" ? "OTC" : "FOREX"}
          </span>
          <span data-testid="signal-chart-badge" className="flex items-center gap-1 text-[11px] font-mono font-extrabold uppercase tracking-wider rounded px-2 py-0.5 text-[#03110B]"
            style={{ background: color }}>
            <Clock className="w-3 h-3" /> M{signal.timeframe} · {t("signal.chartBadge", { tf: signal.timeframe })}
          </span>
        </div>
        <span data-testid="signal-payout-percentage" className="font-mono font-bold text-sm" style={{ color: "#EC1E9C" }}>
          {signal.payout}% {t("signal.payout")}
        </span>
      </div>

      <div className="p-5 flex items-center gap-5">
        <div data-testid="signal-direction-badge" className="flex flex-col items-center justify-center rounded-xl px-6 py-4 min-w-[130px]"
          style={{ background: color, color: "#03110B" }}>
          {isCall ? <ArrowUpRight className="w-8 h-8" strokeWidth={2.5} /> : <ArrowDownRight className="w-8 h-8" strokeWidth={2.5} />}
          <span className="font-heading font-extrabold text-2xl tracking-tight">{signal.direction}</span>
          <span className="text-[11px] font-semibold opacity-80">{isCall ? t("signal.call") : t("signal.put")}</span>
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-3 flex-1">
          <Metric icon={Clock} label={t("signal.timeframe")} value={`${signal.timeframe}m`} testid="signal-timeframe-badge" />
          <Metric icon={Layers} label={t("signal.candles")} value={signal.candle_count} />
          <Metric icon={Activity} label={t("signal.curvature")} value={signal.curvature} />
          <Metric icon={TrendingUp} label={t("signal.expiry")} value={`${signal.expiration_min} ${t("signal.min")}`} />
        </div>
      </div>

      <CandleTimer signal={signal} color={color} t={t} />

      <div className="mx-5 mb-3 rounded-lg border p-3 text-xs" style={{ borderColor: `${color}55`, background: `${color}10` }} data-testid="signal-pattern">
        <span className="font-heading font-bold text-white">{t("signal.patternTitle")}: </span>
        <span className="text-[#94A3B8]">
          {t("signal.pattern", { n: signal.candle_count, color: (signal.streak_color || (isCall ? "red" : "green")) === "green" ? t("signal.green") : t("signal.red"), tf: signal.timeframe, dir: isCall ? t("signal.upward") : t("signal.downward") })}
        </span>
        {signal.recent_candles?.length ? (
          <div className="mt-3" data-testid="signal-pattern-strip">
            <div className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1.5">{t("signal.recentTitle")}</div>
            <div className="flex items-end gap-1.5 flex-wrap">
              {signal.recent_candles.map((c, i) => (
                <div key={i} className="flex flex-col items-center gap-1" title={`${fmtTime(c.t)} · ${c.color} · ${c.counted ? t("signal.counted") : t("signal.notCounted")}`}
                  data-testid={`recent-candle-${i}`} data-counted={c.counted ? "1" : "0"} data-color={c.color}>
                  <span className={`w-4 rounded-[2px] ${c.forming ? "h-7 ring-2 ring-white animate-pulse" : "h-6"} ${c.counted ? "" : "opacity-30"}`}
                    style={{ background: c.color === "green" ? "#00FFC2" : c.color === "red" ? "#EC1E9C" : "#64748B" }} />
                  <span className="text-[9px] font-mono text-[#64748B]">{fmtTime(c.t)}</span>
                </div>
              ))}
              <div className="flex flex-col items-center gap-1 ml-2">
                {isCall ? <ArrowUpRight className="w-6 h-6" style={{ color: "#00FFC2" }} /> : <ArrowDownRight className="w-6 h-6" style={{ color: "#EC1E9C" }} />}
                <span className="font-mono font-bold text-[10px]" style={{ color }}>{signal.direction}</span>
              </div>
            </div>
            <div className="text-[10px] text-[#94A3B8] mt-1 text-right">{t("signal.forming")} {t("signal.countDir")}</div>
          </div>
        ) : (
          <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="signal-pattern-strip">
            {Array.from({ length: Math.min(signal.candle_count, 20) }).map((_, i) => (
              <span key={i} className="w-3 h-5 rounded-[2px]" style={{ background: (signal.streak_color || (isCall ? "red" : "green")) === "green" ? "#00FFC2" : "#EC1E9C" }} />
            ))}
            <span className="mx-1 text-[#94A3B8] text-sm">→</span>
            <span className="font-mono font-bold text-xs" style={{ color }}>{signal.direction}</span>
          </div>
        )}
        {signal.streak_start && signal.streak_end && (
          <div className="mt-1 font-mono text-[11px] text-white" data-testid="signal-pattern-range">
            {t("signal.patternRange", { from: fmtTime(signal.streak_start), to: fmtTime(signal.streak_end), tf: signal.timeframe })}
          </div>
        )}
      </div>

      <div className="mx-5 mb-4 rounded-lg border border-[#243047] bg-[#0A0D14] p-4" data-testid="signal-instructions">
        <div className="flex items-center gap-2 text-sm font-heading font-bold text-white mb-2">
          <BookOpen className="w-4 h-4 text-[#00FFC2]" /> {t("signal.howTitle")}
        </div>
        <ol className="space-y-1.5 text-xs text-[#94A3B8] leading-relaxed list-decimal pl-4">
          <li>{t("signal.how1", { dir: isCall ? t("signal.dirCall") : t("signal.dirPut"), asset: signal.asset_name, tf: signal.timeframe })}</li>
          <li><span className="text-white font-semibold">{t("signal.how2")}</span></li>
          <li>{t("signal.how3", { stakes })}</li>
        </ol>
      </div>

      <div className="px-5 py-2 border-t border-[#243047] flex items-center justify-between text-[11px] font-mono text-[#64748B]">
        <span>{t("signal.entry")} {entry ? entry.toLocaleTimeString(lang === "it" ? "it-IT" : "en-GB") : "-"}</span>
        <span>{t("signal.scan")} {signal.scan_ms} ms</span>
      </div>
    </div>
  );
}

function CandleTimer({ signal, color, t }) {
  const tfMs = signal.timeframe * 60000;
  const start = signal.entry_time ? new Date(signal.entry_time).getTime() : Date.now();
  const candleEnd = Math.floor(start / tfMs) * tfMs + tfMs;
  const [left, setLeft] = useState(candleEnd - Date.now());
  useEffect(() => {
    const id = setInterval(() => setLeft(candleEnd - Date.now()), 1000);
    return () => clearInterval(id);
  }, [candleEnd]);
  const done = left <= 0;
  const secs = Math.max(0, Math.floor(left / 1000));
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  const pct = Math.min(100, Math.max(0, (1 - left / tfMs) * 100));
  return (
    <div className="mx-5 mb-3 rounded-lg border border-[#243047] bg-[#0A0D14] p-3" data-testid="signal-candle-timer">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
          <TimerReset className="w-4 h-4" style={{ color }} />
          {done ? t("signal.timerDone") : <span>{t("signal.timerLabel", { tf: signal.timeframe })} · <span className="text-white">{t("signal.timerHint")}</span></span>}
        </div>
        {!done && <span className="font-mono text-2xl font-extrabold tabular-nums" style={{ color }} data-testid="signal-candle-countdown">{mm}:{ss}</span>}
      </div>
      {!done && (
        <div className="mt-2 h-1.5 rounded-full bg-[#243047] overflow-hidden">
          <div className="h-full rounded-full transition-[width] duration-1000" style={{ width: `${pct}%`, background: `linear-gradient(90deg, #EC1E9C, #00FFC2)` }} />
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-[#00E0B0]" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#64748B]">{label}</div>
        <div data-testid={testid} className="font-mono font-bold text-white text-sm">{value}</div>
      </div>
    </div>
  );
}
